import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  BookUser,
  Search,
  Download,
  Phone,
  Mail,
  Truck,
  Zap,
  Car,
  Filter,
  X,
  Users,
  FileSpreadsheet,
  FileText,
  CheckSquare,
  AlertTriangle,
  Send,
  FileDown,
  Image,
  FolderArchive,
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AnnuairePage() {
  const [loading, setLoading] = useState(true);
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [filteredTrucks, setFilteredTrucks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [specialiteFilter, setSpecialiteFilter] = useState("all");
  const [statutFilter, setStatutFilter] = useState("actif");
  const [specialites, setSpecialites] = useState([]);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showRelanceDialog, setShowRelanceDialog] = useState(false);
  const [sendingRelance, setSendingRelance] = useState(false);

  // Required fields for export
  const REQUIRED_FIELDS = [
    { key: 'nom_food_truck', label: 'Food Truck' },
    { key: 'nom_contact', label: 'Contact' },
    { key: 'telephone', label: 'Téléphone' },
    { key: 'specialites', label: 'Spécialités' },
    { key: 'type_vehicule', label: 'Véhicule' },
    { key: 'plaque_immatriculation', label: 'Plaque' },
    { key: 'puissance_electrique', label: 'kW' },
    { key: 'staff_base', label: 'Staff' },
  ];

  // Check which fields are missing for a food truck
  const getMissingFields = (ft) => {
    const missing = [];
    REQUIRED_FIELDS.forEach(({ key, label }) => {
      const value = ft[key];
      if (!value || (Array.isArray(value) && value.length === 0)) {
        missing.push(label);
      }
    });
    return missing;
  };

  // Get selected food trucks
  const getSelectedTrucks = () => {
    return filteredTrucks.filter(ft => selectedIds.has(ft.id));
  };

  // Get trucks with missing info
  const getTrucksWithMissingInfo = () => {
    return getSelectedTrucks().filter(ft => getMissingFields(ft).length > 0);
  };

  // Toggle selection
  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Select all visible
  const selectAll = () => {
    const allIds = new Set(filteredTrucks.map(ft => ft.id));
    setSelectedIds(allIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  useEffect(() => {
    loadFoodTrucks();
  }, []);

  useEffect(() => {
    filterTrucks();
  }, [foodTrucks, searchTerm, specialiteFilter, statutFilter]);

  const loadFoodTrucks = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/food-trucks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setFoodTrucks(response.data);
      
      // Extract unique specialites
      const allSpecs = new Set();
      response.data.forEach((ft) => {
        if (ft.specialites) {
          ft.specialites.forEach((s) => allSpecs.add(s));
        }
      });
      setSpecialites(Array.from(allSpecs).sort());
    } catch (error) {
      console.error("Error loading food trucks:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const filterTrucks = () => {
    let filtered = [...foodTrucks];

    // Filter by status
    if (statutFilter !== "all") {
      filtered = filtered.filter((ft) => ft.statut === statutFilter);
    }

    // Filter by specialite
    if (specialiteFilter !== "all") {
      filtered = filtered.filter(
        (ft) => ft.specialites && ft.specialites.includes(specialiteFilter)
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (ft) =>
          ft.nom_food_truck?.toLowerCase().includes(term) ||
          ft.nom_contact?.toLowerCase().includes(term) ||
          ft.email?.toLowerCase().includes(term) ||
          ft.telephone?.toLowerCase().includes(term) ||
          ft.plaque_immatriculation?.toLowerCase().includes(term)
      );
    }

    setFilteredTrucks(filtered);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSpecialiteFilter("all");
    setStatutFilter("actif");
  };

  const exportToCSV = () => {
    const headers = [
      "Nom Food Truck",
      "Responsable",
      "Téléphone",
      "Email",
      "Spécialités",
      "Type Véhicule",
      "Plaque Immatriculation",
      "Puissance Élec. (kW)",
      "Staff Base",
      "Statut",
    ];

    const rows = filteredTrucks.map((ft) => [
      ft.nom_food_truck || "",
      ft.nom_contact || "",
      ft.telephone || "",
      ft.email || "",
      (ft.specialites || []).join(", "),
      ft.type_vehicule || "",
      ft.plaque_immatriculation || "",
      ft.puissance_electrique || "",
      ft.staff_base || "",
      ft.statut || "",
    ]);

    const csvContent = [
      headers.join(";"),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(";")),
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `annuaire_food_trucks_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV téléchargé");
  };

  const exportToExcel = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/annuaire/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `annuaire_food_trucks_${new Date().toISOString().split("T")[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch (error) {
      console.error("Export error:", error);
      // Fallback to CSV if Excel export fails
      exportToCSV();
    }
  };

  const exportToPDF = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/annuaire/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `annuaire_food_trucks_${new Date().toISOString().split("T")[0]}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export PDF téléchargé");
    } catch (error) {
      console.error("PDF Export error:", error);
      toast.error("Erreur lors de l'export PDF");
    }
  };

  // Export selected trucks to CSV for client
  const exportSelectedToCSV = () => {
    const selected = getSelectedTrucks();
    if (selected.length === 0) {
      toast.error("Veuillez sélectionner au moins un food truck");
      return;
    }

    const headers = [
      "Food Truck",
      "Contact",
      "Téléphone",
      "Spécialités",
      "Véhicule",
      "Plaque",
      "kW",
      "Staff",
      "Statut",
    ];

    const rows = selected.map((ft) => [
      ft.nom_food_truck || "",
      ft.nom_contact || "",
      ft.telephone || "",
      (ft.specialites || []).join(", "),
      ft.type_vehicule || "",
      ft.plaque_immatriculation || "",
      ft.puissance_electrique || "",
      ft.staff_base || "",
      ft.statut || "",
    ]);

    const csvContent = [
      headers.join(";"),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(";")),
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `selection_food_trucks_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    setShowExportDialog(false);
    toast.success(`${selected.length} food truck(s) exporté(s)`);
  };

  // Export selected to PDF for client
  const exportSelectedToPDF = async () => {
    const selected = getSelectedTrucks();
    if (selected.length === 0) {
      toast.error("Veuillez sélectionner au moins un food truck");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/annuaire/export-selection-pdf`,
        { food_truck_ids: selected.map(ft => ft.id) },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `selection_food_trucks_${new Date().toISOString().split("T")[0]}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      setShowExportDialog(false);
      toast.success(`${selected.length} food truck(s) exporté(s) en PDF`);
    } catch (error) {
      console.error("PDF Export error:", error);
      // Fallback to CSV
      exportSelectedToCSV();
    }
  };

  // Send reminder emails to trucks with missing info
  const sendRelanceEmails = async () => {
    const trucksWithMissing = getTrucksWithMissingInfo();
    if (trucksWithMissing.length === 0) {
      toast.info("Tous les food trucks sélectionnés ont leurs informations complètes");
      return;
    }

    setSendingRelance(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/annuaire/relance-infos`,
        { 
          food_truck_ids: trucksWithMissing.map(ft => ft.id),
          missing_fields: trucksWithMissing.map(ft => ({
            id: ft.id,
            email: ft.email,
            nom: ft.nom_food_truck,
            missing: getMissingFields(ft)
          }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${response.data.emails_sent || trucksWithMissing.length} email(s) de relance envoyé(s)`);
      setShowRelanceDialog(false);
    } catch (error) {
      console.error("Relance error:", error);
      toast.error("Erreur lors de l'envoi des relances");
    } finally {
      setSendingRelance(false);
    }
  };

  // Export photos/album of selected food trucks
  const [exportingPhotos, setExportingPhotos] = useState(false);
  
  const exportPhotosZip = async () => {
    const selected = getSelectedTrucks();
    if (selected.length === 0) {
      toast.error("Veuillez sélectionner au moins un food truck");
      return;
    }

    // Check if any selected truck has photos
    const trucksWithPhotos = selected.filter(ft => ft.photos && Object.keys(ft.photos).length > 0);
    if (trucksWithPhotos.length === 0) {
      toast.error("Aucun des food trucks sélectionnés n'a de photos");
      return;
    }

    setExportingPhotos(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/annuaire/export-photos`,
        { food_truck_ids: selected.map(ft => ft.id) },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `photos_food_trucks_${new Date().toISOString().split("T")[0]}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      setShowExportDialog(false);
      toast.success(`Photos exportées (${trucksWithPhotos.length} food truck(s))`);
    } catch (error) {
      console.error("Photos Export error:", error);
      toast.error("Erreur lors de l'export des photos");
    } finally {
      setExportingPhotos(false);
    }
  };

  // Get count of trucks with photos
  const getTrucksWithPhotos = () => {
    return getSelectedTrucks().filter(ft => {
      const photos = ft.photos;
      if (!photos || typeof photos !== 'object') return false;
      // Check if any category has photos
      return Object.values(photos).some(arr => Array.isArray(arr) && arr.length > 0);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0" data-testid="annuaire-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-secondary flex items-center gap-2">
              <BookUser className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              Annuaire
            </h1>
            <p className="text-muted-foreground text-xs sm:text-sm">
              Carnet d'adresses des adhérents
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Selection actions */}
            {selectedIds.size > 0 && (
              <>
                <Badge variant="secondary" className="px-3 py-1">
                  <CheckSquare className="w-3 h-3 mr-1" />
                  {selectedIds.size} sélectionné(s)
                </Badge>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowExportDialog(true)}
                  className="text-xs sm:text-sm border-green-300 text-green-700 hover:bg-green-50"
                >
                  <FileDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Exporter sélection
                </Button>
                {getTrucksWithMissingInfo().length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowRelanceDialog(true)}
                    className="text-xs sm:text-sm border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <Send className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Relancer ({getTrucksWithMissingInfo().length})
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearSelection}
                  className="text-xs sm:text-sm"
                >
                  <X className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={exportToPDF} className="text-xs sm:text-sm">
              <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              <span className="hidden sm:inline">Export </span>PDF
            </Button>
            <Button variant="outline" size="sm" onClick={exportToCSV} className="text-xs sm:text-sm">
              <FileSpreadsheet className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              <span className="hidden sm:inline">Export </span>CSV
            </Button>
            <Button size="sm" onClick={exportToExcel} className="text-xs sm:text-sm">
              <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-secondary">
                  {foodTrucks.filter((ft) => ft.statut === "actif").length}
                </p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Actifs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Truck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">
                  {foodTrucks.length}
                </p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">
                  {foodTrucks.filter((ft) => ft.telephone).length}
                </p>
                <p className="text-xs text-muted-foreground">Avec téléphone</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Zap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">
                  {foodTrucks.filter((ft) => ft.besoin_electrique || ft.puissance_electrique).length}
                </p>
                <p className="text-xs text-muted-foreground">Besoin élec.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={statutFilter} onValueChange={setStatutFilter}>
                <SelectTrigger className="w-[120px] sm:w-[150px] text-xs sm:text-sm">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="actif">Actifs</SelectItem>
                  <SelectItem value="inactif">Inactifs</SelectItem>
                  <SelectItem value="en_attente_validation">En attente</SelectItem>
                </SelectContent>
              </Select>
              <Select value={specialiteFilter} onValueChange={setSpecialiteFilter}>
                <SelectTrigger className="w-[140px] sm:w-[180px] text-xs sm:text-sm">
                  <SelectValue placeholder="Spécialité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  {specialites.map((spec) => (
                    <SelectItem key={spec} value={spec}>
                      {spec}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(searchTerm || specialiteFilter !== "all" || statutFilter !== "actif") && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
                  <X className="w-3 h-3 mr-1" />
                  Effacer
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
        <Filter className="w-3 h-3 sm:w-4 sm:h-4" />
        {filteredTrucks.length} food truck(s)
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-[900px]">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-10">
                    <Checkbox 
                      checked={filteredTrucks.length > 0 && selectedIds.size === filteredTrucks.length}
                      onCheckedChange={(checked) => checked ? selectAll() : clearSelection()}
                    />
                  </TableHead>
                  <TableHead className="font-semibold text-xs sm:text-sm whitespace-nowrap">Food Truck</TableHead>
                  <TableHead className="font-semibold text-xs sm:text-sm whitespace-nowrap">Contact</TableHead>
                  <TableHead className="font-semibold text-xs sm:text-sm whitespace-nowrap">Tél.</TableHead>
                  <TableHead className="font-semibold text-xs sm:text-sm whitespace-nowrap">Spécialités</TableHead>
                  <TableHead className="font-semibold text-xs sm:text-sm whitespace-nowrap">Véhicule</TableHead>
                  <TableHead className="font-semibold text-xs sm:text-sm whitespace-nowrap">Plaque</TableHead>
                  <TableHead className="font-semibold text-xs sm:text-sm whitespace-nowrap">kW</TableHead>
                  <TableHead className="font-semibold text-xs sm:text-sm whitespace-nowrap">Staff</TableHead>
                  <TableHead className="font-semibold text-xs sm:text-sm whitespace-nowrap">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTrucks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground text-sm">
                      Aucun food truck trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTrucks.map((ft) => {
                    const missingFields = getMissingFields(ft);
                    const hasMissing = missingFields.length > 0;
                    return (
                    <TableRow 
                      key={ft.id} 
                      className={`hover:bg-muted/30 ${selectedIds.has(ft.id) ? 'bg-primary/5' : ''}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Checkbox 
                            checked={selectedIds.has(ft.id)}
                            onCheckedChange={() => toggleSelection(ft.id)}
                          />
                          {hasMissing && (
                            <AlertTriangle className="w-3 h-3 text-amber-500" title={`Manque: ${missingFields.join(', ')}`} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm">
                        <div className="flex items-center gap-2">
                          <Truck className="w-3 h-3 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                          <span className="font-medium whitespace-nowrap">{ft.nom_food_truck || <span className="text-amber-500">-</span>}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm">{ft.nom_contact || <span className="text-amber-500">-</span>}</TableCell>
                      <TableCell className="text-xs sm:text-sm">
                        {ft.telephone ? (
                          <a
                            href={`tel:${ft.telephone}`}
                            className="flex items-center gap-1 text-primary hover:underline whitespace-nowrap"
                          >
                            <Phone className="w-3 h-3" />
                            {ft.telephone}
                          </a>
                        ) : (
                          <span className="text-amber-500">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm">
                        {ft.specialites?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {ft.specialites?.slice(0, 2).map((spec, idx) => (
                            <Badge key={idx} variant="outline" className="text-[10px] sm:text-xs">
                              {spec}
                            </Badge>
                          ))}
                          {ft.specialites?.length > 2 && (
                            <Badge variant="outline" className="text-[10px] sm:text-xs">
                              +{ft.specialites.length - 2}
                            </Badge>
                          )}
                        </div>
                        ) : (
                          <span className="text-amber-500">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm">
                        {ft.type_vehicule ? (
                          <Badge variant="outline" className={`text-[10px] sm:text-xs ${
                            ft.type_vehicule === "Camion" 
                              ? "bg-blue-50 text-blue-700 border-blue-200" 
                              : "bg-purple-50 text-purple-700 border-purple-200"
                          }`}>
                            {ft.type_vehicule === "Camion" ? (
                              <Truck className="w-3 h-3 mr-1" />
                            ) : (
                              <Car className="w-3 h-3 mr-1" />
                            )}
                            {ft.type_vehicule}
                          </Badge>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm">
                        {ft.plaque_immatriculation ? (
                          <span className="font-mono text-[10px] sm:text-xs bg-gray-100 px-1 py-0.5 rounded whitespace-nowrap">
                            {ft.plaque_immatriculation}
                          </span>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm">
                        {ft.puissance_electrique ? (
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            <Zap className="w-3 h-3 text-amber-500" />
                            <span className="font-medium">
                              {ft.puissance_electrique}
                            </span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm">
                        {ft.staff_base ? (
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3 text-blue-500" />
                            <span className="text-sm font-medium">
                              {ft.staff_base}
                            </span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={ft.statut === "actif" ? "default" : "secondary"}
                          className={
                            ft.statut === "actif"
                              ? "bg-green-100 text-green-700"
                              : ft.statut === "en_attente_validation"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-gray-100 text-gray-700"
                          }
                        >
                          {ft.statut === "actif"
                            ? "Actif"
                            : ft.statut === "en_attente_validation"
                            ? "En attente"
                            : "Inactif"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Export Selection Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exporter la sélection ({selectedIds.size} food truck(s))</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Choisissez le format d'export pour envoyer au client :
            </p>
            
            {getTrucksWithMissingInfo().length > 0 && (
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <strong>{getTrucksWithMissingInfo().length}</strong> food truck(s) avec infos manquantes
                </p>
                <ul className="mt-2 text-xs text-amber-600 space-y-1">
                  {getTrucksWithMissingInfo().slice(0, 3).map(ft => (
                    <li key={ft.id}>• {ft.nom_food_truck}: {getMissingFields(ft).join(', ')}</li>
                  ))}
                  {getTrucksWithMissingInfo().length > 3 && (
                    <li>• ... et {getTrucksWithMissingInfo().length - 3} autre(s)</li>
                  )}
                </ul>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={exportSelectedToPDF} className="flex-1">
                <FileText className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
              <Button variant="outline" onClick={exportSelectedToCSV} className="flex-1">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
            
            {/* Export Photos */}
            {getTrucksWithPhotos().length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-2">
                  <Image className="w-4 h-4 inline mr-1" />
                  {getTrucksWithPhotos().length} food truck(s) avec photos
                </p>
                <Button 
                  variant="outline" 
                  onClick={exportPhotosZip} 
                  disabled={exportingPhotos}
                  className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  {exportingPhotos ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-700 mr-2" />
                      Export en cours...
                    </>
                  ) : (
                    <>
                      <FolderArchive className="w-4 h-4 mr-2" />
                      Exporter les photos (ZIP)
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Relance Dialog */}
      <Dialog open={showRelanceDialog} onOpenChange={setShowRelanceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Relancer les food trucks</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Envoyer un email de relance aux food trucks avec des informations manquantes :
            </p>
            
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {getTrucksWithMissingInfo().map(ft => (
                <div key={ft.id} className="bg-gray-50 p-3 rounded-lg border">
                  <p className="font-medium text-sm">{ft.nom_food_truck}</p>
                  <p className="text-xs text-muted-foreground">{ft.email}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {getMissingFields(ft).map((field, idx) => (
                      <Badge key={idx} variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                        {field}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRelanceDialog(false)}>
                Annuler
              </Button>
              <Button onClick={sendRelanceEmails} disabled={sendingRelance}>
                {sendingRelance ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer ({getTrucksWithMissingInfo().length} email(s))
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
